import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { MealsWorkspaceRepository } from '../domain/meals-workspace.repository';
import {
  ActualConsumptionInput,
  CookingSessionInput,
  FoodIngredient,
  FoodRecipe,
  IngredientInput,
  MealsWorkspace,
  ShoppingItemInput
} from '../models/meals-workspace.model';

@Injectable()
export class SupabaseMealsWorkspaceRepository extends MealsWorkspaceRepository {
  private readonly client: SupabaseClient;

  constructor(private readonly supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async load(): Promise<MealsWorkspace> {
    const [ingredients, recipes, recipeIngredients, sessions, sessionRecipes, sessionIngredients,
      consumptions, consumptionLines, shoppingLists, plannedConsumptions] = await Promise.all([
      this.client.from('meal_ingredients').select('*').order('name'),
      this.client.from('meal_recipes').select('*').order('title'),
      this.client.from('meal_recipe_ingredients').select('*'),
      this.client.from('meal_cooking_sessions').select('*').order('planned_on', { ascending: false }),
      this.client.from('meal_cooking_session_recipes').select('*'),
      this.client.from('meal_cooking_session_ingredients').select('*'),
      this.client.from('meal_consumptions').select('id,consumed_on,axis_id').order('consumed_on', { ascending: false }),
      this.client.from('meal_consumption_lines').select('*'),
      this.client.from('lists').select('id,name').eq('is_archived', false).eq('kind', 'CHECKLIST').order('name'),
      this.client.from('meal_planned_consumptions').select('id,planned_on,axis_id,title,lines,consumption_id').order('planned_on',{ascending:true})
    ]);

    for (const result of [ingredients, recipes, recipeIngredients, sessions, sessionRecipes,
      sessionIngredients, consumptions, consumptionLines, shoppingLists, plannedConsumptions]) {
      if (result.error) throw result.error;
    }

    return {
      ingredients: (ingredients.data ?? []) as FoodIngredient[],
      recipes: (recipes.data ?? []) as FoodRecipe[],
      recipeIngredients: recipeIngredients.data ?? [],
      sessions: sessions.data ?? [],
      sessionRecipes: sessionRecipes.data ?? [],
      sessionIngredients: sessionIngredients.data ?? [],
      consumptions: consumptions.data ?? [],
      plannedConsumptions: plannedConsumptions.data ?? [],
      consumptionLines: consumptionLines.data ?? [],
      shoppingLists: shoppingLists.data ?? []
    };
  }

  async saveIngredient(input: IngredientInput, id?: number): Promise<number> {
    const query = id
      ? this.client.from('meal_ingredients').update(input).eq('id', id)
      : this.client.from('meal_ingredients').insert(input);
    const { data, error } = await query.select('id').single();
    if (error) throw error;
    return Number(data.id);
  }

  async saveRecipe(
    recipe: Pick<FoodRecipe, 'title' | 'instructions'>,
    ingredients: { ingredient_id: number; quantity: number | null }[],
    id?: number
  ): Promise<void> {
    const { error } = await this.client.rpc('save_meal_recipe', {
      p_id: id ?? null,
      p_title: recipe.title.trim(),
      p_instructions: recipe.instructions,
      p_ingredients: ingredients
    });
    if (error) throw error;
  }

  async listMealAxes(): Promise<Array<{id:number;name:string}>> {
    const {data,error}=await this.client.from('calendar_axes')
      .select('id,name').eq('kind','MEAL').eq('is_archived',false).order('position');
    if (error) throw error;
    return (data ?? []).map(axis => ({id:Number(axis.id),name:String(axis.name)}));
  }

  async createSession(input: CookingSessionInput): Promise<number> {
    const { data, error } = await this.client.rpc('create_meal_cooking_session', {
      p_date: input.date,
      p_title: input.title,
      p_servings: input.producedServings,
      p_recipes: input.recipes,
      p_ingredients: []
    });
    if (error) throw error;
    const sessionId = Number(data);
    if (input.axisId != null) {
      const { data: updated, error: axisError } = await this.client.from('meal_cooking_sessions')
        .update({ axis_id: input.axisId }).eq('id', sessionId)
        .select('id').single();
      if (axisError || !updated) {
        // Compensate if the chosen meal slot could not be stored.
        await this.client.from('meal_cooking_sessions').delete().eq('id',sessionId);
        throw axisError ?? new Error('Meal slot could not be assigned');
      }
    }
    return sessionId;
  }

  async updateSession(input: CookingSessionInput): Promise<void> {
    if (!input.id) throw new Error('Missing session id');
    const { error } = await this.client.rpc('update_meal_cooking_session', {
      p_session_id: input.id,
      p_date: input.date,
      p_recipes: input.recipes
    });
    if (error) throw error;
    const { error: axisError } = await this.client.from('meal_cooking_sessions')
      .update({ axis_id: input.axisId ?? null }).eq('id', input.id);
    if (axisError) throw axisError;
  }

  async setSessionCooked(
    id: number, cooked: boolean,
    weights: { session_recipe_id: number; weight_g: number }[] = []
  ): Promise<void> {
    const { error } = await this.client.rpc('set_meal_session_cooked', {
      p_session_id: id,
      p_cooked: cooked,
      p_dish_weights: weights
    });
    if (error) throw error;
  }

  async savePlannedConsumption(input: ActualConsumptionInput, title: string, id?: number, axisId?: number | null): Promise<void> {
    const ownerId = this.supabase.userId;
    if (!ownerId) throw new Error('Authentication required');
    const payload = { planned_on: input.date, axis_id: axisId ?? null, title: title.trim(), lines: input.lines };
    const query = id ? this.client.from('meal_planned_consumptions').update(payload).eq('id', id).eq('owner_id', ownerId).is('consumption_id',null)
      : this.client.from('meal_planned_consumptions').insert({ ...payload, owner_id: ownerId });
    const {data,error} = await query.select('id').single();
    if (error) throw error;
    if (!data) throw new Error('Planned meal not saved');
  }

  async confirmPlannedConsumption(id: number, input: ActualConsumptionInput): Promise<void> {
    const {error} = await this.client.rpc('confirm_planned_meal',{
      p_plan_id:id,p_date:input.date,p_lines:input.lines
    });
    if (error) throw error;
  }

  async recordConsumption(input: ActualConsumptionInput): Promise<void> {
    const { data, error } = await this.client.rpc('log_meal_consumption', {
      p_date: input.date,
      p_lines: input.lines
    });
    if (error) throw error;
    if (input.axisId != null) {
      const { error: axisError } = await this.client.from('meal_consumptions')
        .update({ axis_id: input.axisId }).eq('id',Number(data)).eq('owner_id',this.supabase.userId);
      if (axisError) throw axisError;
    }
  }

  async updateConsumption(id: number, input: ActualConsumptionInput): Promise<void> {
    const { error } = await this.client.rpc('update_meal_consumption', {
      p_consumption_id: id,
      p_date: input.date,
      p_lines: input.lines
    });
    if (error) throw error;
    if (input.axisId !== undefined) {
      const { error: axisError } = await this.client.from('meal_consumptions')
        .update({ axis_id: input.axisId }).eq('id',id).eq('owner_id',this.supabase.userId);
      if (axisError) throw axisError;
    }
  }

  async deleteEntity(
    kind: 'ingredient' | 'recipe' | 'consumption' | 'session',
    id: number
  ): Promise<void> {
    const table = {
      ingredient: 'meal_ingredients',
      recipe: 'meal_recipes',
      consumption: 'meal_consumptions',
      session: 'meal_cooking_sessions'
    }[kind];

    const { error } = await this.client.from(table).delete().eq('id', id);
    if (error) throw error;
  }

  async createShoppingList(name: string): Promise<number> {
    const ownerId = this.supabase.userId;
    if (!ownerId) throw new Error('Authentication required');
    const { data, error } = await this.client.from('lists')
      .insert({
        owner_id: ownerId,
        name: name.trim(),
        kind: 'CHECKLIST',
        description: null,
        is_archived: false,
        archived_at: null
      })
      .select('id')
      .single();
    if (error) throw error;
    return Number(data.id);
  }

  async addShoppingItems(listId: number, items: ShoppingItemInput[]): Promise<number> {
    const { data, error } = await this.client.rpc('add_meal_shopping_items', {
      p_list_id: listId,
      p_items: items
    });
    if (error) throw error;
    return Number(data);
  }
}
